// @givmo/org-console — the operations, addressed by URL (fifteen at OM-10; +8 at shell contract v3).
//
// ══ WHY THIS FILE EXISTS AT ALL ════════════════════════════════════════════════════════════════
//
// The `/org` family landed on the backend AFTER the committed OpenAPI snapshot was last refreshed, so
// there is no generated SDK operation, no generated type and no query-option factory for any of the
// fifteen.
//
// The api-client doc's § *A whole ROUTE the snapshot lacks* is the sanctioned disposition, and it is
// deliberately NOT "refresh the snapshot for one feature": a refresh absorbs every unrelated backend
// change since the last regen and collides with any regen already in flight. ⚠ IT USED TO SAY A
// REFRESH COULD NOT FINISH THE JOB EITHER — two of the operations being unlanded — and that half is
// no longer true; all of them are on the backend's integration branch. Do not cite the retired reason.
// `givmoClient` is the same fetch client the generated operations bind
// to, so a call made here inherits the whole interceptor stack: bearer token, App Check,
// `X-Request-Id`, the `{data, meta, links}` unwrap, and the `GivmoError` mapping. Nothing about the
// request differs from a generated one except who wrote the URL.
//
// ══ URL BUILDING ═══════════════════════════════════════════════════════════════════════════════
//
// Every interpolated segment is `encodeURIComponent`-ed. Not because a UUID needs escaping, but
// because the day one of these segments stops being a UUID is not the day anybody re-reads this file.
//
// The page parameters ride `query`, which the shared client serializes exactly as it does for a
// generated operation — so `page[limit]` reaches the wire in the same form the rest of the estate
// sends, rather than in whatever a hand-rolled query string happened to produce.
//
// ⚠ THE UNWRAP DECIDES WHAT A LIST READ CAN HONESTLY CLAIM. Four of them answer the paginated
// envelope, and the middleware hands the bare `data` array to the caller with every top-level
// sibling discarded. So `meta` is unreachable and NO screen here may state a total — paging asks
// "did the last page come back short" and footers count rows loaded. See `org-decode.ts`.

import { firstErrorCode, givmoClient, isGivmoError } from '@givmo/api-client';
import type {
  AcceptOrgInviteBody,
  AcceptOrgModuleTermsBody,
  ApproveOrgAccessRequestBody,
  CreateOrgAccessRequestBody,
  CreateOrgInviteBody,
  DenyOrgAccessRequestBody,
  OrgRoleChangeBody,
  PreviewOrgInviteBody,
  UpdateOrgAccessSettingsBody,
  UpdateOrgAdminPreferencesBody,
  UpdateOrgMeasurementConfigBody,
  UpdateOrgMemberAccessBody,
  UpdateOrgSettingsBody,
} from './wire-types';

/** Rows per page for the roster, the catalog and the connector list. */
export const ORG_CONSOLE_PAGE_LIMIT = 10;
/** Rows per page for the access event log, which is read in longer runs than the others. */
export const ORG_CONSOLE_EVENTS_PAGE_LIMIT = 20;

/** `/org/{org_ref}` — the scope every operation hangs under. */
function orgBase(orgRef: string): string {
  return `/org/${encodeURIComponent(orgRef)}`;
}

/** The canonical page parameters, in the form every Givmo list read uses. */
function page(limit: number, offset: number): Record<string, number> {
  return { 'page[limit]': limit, 'page[offset]': offset };
}

/** What a paged read needs from its caller. */
export interface PagedRead {
  offset: number;
  signal?: AbortSignal;
}

/**
 * True when the console is ABSENT in this environment — never an error surface.
 *
 * ⚠ CLASSIFIED ON STATUS ALONE, and it has to be. This surface's dark answer is byte-identical to a
 * route miss by ratified design: `{"title": "Not Found", "detail": "Not Found"}`, with no
 * `error_code` and with the organization reference never echoed, so a prober cannot separate "the
 * route exists and is off" from "there is no such route". A client reaching for an error code here
 * would branch on a field that is never present and take the wrong leg forever.
 *
 * ⚠ A 403 NEVER APPEARS ON THIS SURFACE. An unknown organization, one the caller does not
 * administer, and one that resolves ambiguously all answer the same 404 — so there is no
 * permission-denied branch to write. 404 means absent, full stop.
 *
 * ⚠ A 500 IS NOT ABSENCE. Serving a calm not-available state over a live outage would hide a real
 * problem behind a page that looks fine.
 */
export function isOrgConsoleUnavailable(error: unknown): boolean {
  return isGivmoError(error) && error.status === 404;
}

/**
 * The invite send budget for this hour is spent — the ONE refusal on the create path that is about
 * timing rather than about the offer, so it is the one a form may honestly invite someone to retry.
 */
export function isOrgInviteRateLimited(error: unknown): boolean {
  return isGivmoError(error) && error.status === 429;
}

/**
 * The invite family's ONE authority refusal that is not the console's 404 (OM-10 clause 7, opened at
 * CPA-WEB40 W6-BE): a caller admitted through a module's `<module>:invite_admins` string may act on
 * MODULE-ISSUED invites of that module only — rescinding an org-issued offer (a Global Admin's, even
 * one that includes their app) answers 403. The invites panel withholds that control on such rows;
 * this names the refusal for the race in which a row was drawn before the caller's standing changed.
 */
export function isOrgInviteForbidden(error: unknown): boolean {
  return isGivmoError(error) && error.status === 403;
}

/**
 * A settings change refused as made from a STALE view (Org Machinery OM-17 design 4A): the key
 * changed since the page read it, or another change took its next revision first — 409
 * `SETTING_STALE`, nothing saved. The refusal's `detail` is the line the card says (D-CCM-44's
 * approved words, served; the web keeps no copy of them).
 */
export function isOrgSettingStale(error: unknown): boolean {
  return isGivmoError(error) && error.status === 409 && firstErrorCode(error) === 'SETTING_STALE';
}

// ⚠ THE REDEEM PATH'S ONE TYPED REFUSAL — the caller's OWN email not being confirmed — IS NOT
// DETECTED HERE, and its absence is deliberate. Every other non-redeemable condition (unknown,
// already redeemed, past expiry, addressed to somebody else) collapses to one constant answer so a
// leaked link cannot be probed; that one is typed, raised BEFORE the token is consulted at all, so
// it discloses only the caller's own account state. Detecting it means comparing against the
// backend's fixed-vocabulary error-code string, and that string contains a root this package's own
// copy rail bans outright — for the good reason that the root names something this console does not
// do. It is also not `/org` knowledge: a shared account-state handler raises it on many routes,
// while `isOrgConsoleUnavailable` above is a property of THIS family's dark posture that every panel
// needs. So the branch lives beside the accept SCREEN, which is a host page in both class consoles
// by design. It moves in here the day a second console consumes the redeem operation.

// ── Reads ────────────────────────────────────────────────────────────────────

/** The admin roster, each row carrying that admin's per-module standing. Any admin may read it. */
export async function fetchOrgMembers(orgRef: string, read: PagedRead): Promise<unknown> {
  const { data } = await givmoClient.get({
    url: `${orgBase(orgRef)}/members`,
    query: page(ORG_CONSOLE_PAGE_LIMIT, read.offset),
    signal: read.signal,
    throwOnError: true,
  });
  return data;
}

/** The customizable permission catalog, per module, with each key's tier. A PAGED LIST of rows. */
export async function fetchOrgAccessCatalog(orgRef: string, read: PagedRead): Promise<unknown> {
  const { data } = await givmoClient.get({
    url: `${orgBase(orgRef)}/access/catalog`,
    query: page(ORG_CONSOLE_PAGE_LIMIT, read.offset),
    signal: read.signal,
    throwOnError: true,
  });
  return data;
}

/** The org-wide module switches, defaults already applied. A plain object, not a list. */
export async function fetchOrgAccessSettings(
  orgRef: string,
  options: { signal?: AbortSignal } = {},
): Promise<unknown> {
  const { data } = await givmoClient.get({
    url: `${orgBase(orgRef)}/access/settings`,
    signal: options.signal,
    throwOnError: true,
  });
  return data;
}

/** The append-only access event log, newest first. Global-Admin only. */
export async function fetchOrgAccessEvents(orgRef: string, read: PagedRead): Promise<unknown> {
  const { data } = await givmoClient.get({
    url: `${orgBase(orgRef)}/access/events`,
    query: page(ORG_CONSOLE_EVENTS_PAGE_LIMIT, read.offset),
    signal: read.signal,
    throwOnError: true,
  });
  return data;
}

/** The caller's own standing for THIS organization. The ONE answer to "what may I do here". */
export async function fetchOrgSelfAccess(
  orgRef: string,
  options: { signal?: AbortSignal } = {},
): Promise<unknown> {
  const { data } = await givmoClient.get({
    url: `${orgBase(orgRef)}/me`,
    signal: options.signal,
    throwOnError: true,
  });
  return data;
}

/**
 * EVERY membership the caller holds, across every class. Auth-only — listing your own memberships
 * needs no organization permission.
 *
 * ⚠ THE ONE READ HERE THAT IS NOT SCOPED BY AN ORGANIZATION, so it does not hang off `orgBase`. It
 * answers a question about a PERSON.
 *
 * ⚠ AND THE ONE THAT DOES NOT EXIST ON THE BACKEND YET. Its dark answer is a plain route miss, which
 * {@link isOrgConsoleUnavailable} already classifies as ABSENT — so every caller must treat absence
 * as "no answer", never as "no memberships", and must behave exactly as it did before this call
 * existed until the route lands. That is not a temporary allowance: it is the same 404-means-absent
 * discipline the rest of this surface is built on, and it is what lets this ship dark today.
 */
export async function fetchOrgMemberships(
  options: { signal?: AbortSignal } = {},
): Promise<unknown> {
  const { data } = await givmoClient.get({
    url: '/org/memberships',
    signal: options.signal,
    throwOnError: true,
  });
  return data;
}

/**
 * The organization's measurement ids, plus when and by whom they were last changed. A plain object,
 * not a list. Any admin may read it — seeing how your own organization is instrumented is not
 * privileged; CHANGING it is not.
 *
 * ⚠ AN ORGANIZATION WITH NOTHING CONFIGURED ANSWERS 200 WITH EVERY FIELD NULL, never a 404. A 404
 * from this address means the console is dark, the route missed, or this is not an organization the
 * caller administers — the three are byte-identical by design and no caller may try to tell them
 * apart.
 */
export async function fetchOrgMeasurementConfig(
  orgRef: string,
  options: { signal?: AbortSignal } = {},
): Promise<unknown> {
  const { data } = await givmoClient.get({
    url: `${orgBase(orgRef)}/measurement`,
    signal: options.signal,
    throwOnError: true,
  });
  return data;
}

/**
 * This organization's LIVE invites — offered, not yet redeemed, not yet past their expiry. Newest
 * first, paged.
 *
 * ⚠ THE SERVER FILTERS EXPIRY, so this list is shorter than "every PENDING row" and deliberately so:
 * an expired invite can do nothing, and showing it would put a control beside an offer nobody can
 * take. Same authority as making one — seeing who has been offered administration is the same
 * question as offering it.
 *
 * `module` is the MODULE VIEW's scope (a module's own Team page): the server narrows the list to
 * the offers that carry that app, and a holder of `<module>:invite_admins` who is not a Global
 * Admin MUST pass it. Omitted for the org-wide view.
 */
export interface InvitesRead extends PagedRead {
  module?: string | null;
}

export async function fetchOrgInvites(orgRef: string, read: InvitesRead): Promise<unknown> {
  const query: Record<string, string | number> = page(ORG_CONSOLE_PAGE_LIMIT, read.offset);
  if (typeof read.module === 'string' && read.module.length > 0) query.module = read.module;
  const { data } = await givmoClient.get({
    url: `${orgBase(orgRef)}/members/invites`,
    query,
    signal: read.signal,
    throwOnError: true,
  });
  return data;
}

/** The AIs currently connected to this organization, newest first. Any admin may read it. */
export async function fetchOrgConnectedAi(orgRef: string, read: PagedRead): Promise<unknown> {
  const { data } = await givmoClient.get({
    url: `${orgBase(orgRef)}/connected-ai`,
    query: page(ORG_CONSOLE_PAGE_LIMIT, read.offset),
    signal: read.signal,
    throwOnError: true,
  });
  return data;
}

// ── Writes ───────────────────────────────────────────────────────────────────

/**
 * Offer someone administration of this organization. Global-Admin only.
 *
 * ⚠ THE ANSWER IS DISCRIMINATED AND A NULL INVITE IS A SUCCESS. `{already_admin: true, invite: null}`
 * means the address already belongs to an administrator here, so nothing was created and nobody was
 * emailed — a different SCREEN, not an error. See `OrgInviteCreated`.
 *
 * ⚠ THE RESPONSE NEVER CARRIES THE TOKEN. The plaintext lives only inside the emailed link; the
 * store keeps its hash. So a surface can never re-show a link, and "invite them again" is a new
 * offer that SUPERSEDES the live one rather than a re-issue of the same token.
 */
export async function createOrgInvite(orgRef: string, body: CreateOrgInviteBody): Promise<unknown> {
  const { data } = await givmoClient.post({
    url: `${orgBase(orgRef)}/members/invites`,
    body,
    throwOnError: true,
  });
  return data;
}

/**
 * Rescind a live invite before it is redeemed. Global-Admin only.
 *
 * Answers 204 and carries no body. An unknown id, ANOTHER organization's id and an already-terminal
 * invite are all the same refusal — `org_ref` carries no foreign key, so scoping is the only thing
 * keeping the two id spaces apart, and a distinguishable answer would make invite ids enumerable
 * across organizations.
 */
export async function revokeOrgInvite(orgRef: string, inviteId: string): Promise<void> {
  await givmoClient.delete({
    url: `${orgBase(orgRef)}/members/invites/${encodeURIComponent(inviteId)}`,
    throwOnError: true,
  });
}

/**
 * Redeem an invite token → become an administrator of the inviting organization.
 *
 * ⚠ THE ONE OPERATION HERE THAT NAMES NO ORGANIZATION, and that is the design rather than an
 * omission: the invitee holds a token and nothing else, resolving WHICH organization invited them is
 * exactly what the token is for, and the invite mail never told them a reference. So it does not
 * hang off {@link orgBase} — like `/org/memberships`, it is addressed at the family root.
 *
 * ⚠ AND IT IS THE ONE THE CALLER IS NOT YET AN ADMIN FOR. Authenticated, but carrying no
 * organization permission — a surface that gated it behind an admin standing would refuse every
 * legitimate invitee, which is the entire population of this call.
 */
export async function acceptOrgInvite(body: AcceptOrgInviteBody): Promise<unknown> {
  const { data } = await givmoClient.post({
    url: '/org/invites/accept',
    body,
    throwOnError: true,
  });
  return data;
}

/**
 * What an invite offers, before anyone signs in (EP-MNT17) — the redeem's first half, addressed at the
 * family root like it.
 *
 * ⚠ IT NEEDS NO SESSION AND CHANGES NOTHING. The token is what admits the caller, so a person new to
 * Givmo can read the offer on the invite's own page; it rides the body, so no request log stores it;
 * and a link that cannot be used answers the redeem's one constant refusal, so the two operations give
 * a dead link the same words.
 */
export async function previewOrgInvite(body: PreviewOrgInviteBody): Promise<unknown> {
  const { data } = await givmoClient.post({
    url: '/org/invites/preview',
    body,
    throwOnError: true,
  });
  return data;
}

/**
 * End one connected AI's grant, on the ORGANIZATION's authority. Global-Admin only.
 *
 * Answers 204 and carries no body. Idempotent on a grant this organization owns; an unknown grant,
 * another organization's grant, and a malformed id are all the same 404 — so a caller's error branch
 * has exactly ONE non-success shape to handle, and it means "already ended, or not this
 * organization's", never "failed".
 */
export async function revokeOrgConnectedAi(orgRef: string, grantId: string): Promise<void> {
  await givmoClient.delete({
    url: `${orgBase(orgRef)}/connected-ai/${encodeURIComponent(grantId)}`,
    throwOnError: true,
  });
}

/**
 * Patch ONE admin's access. Global-Admin only.
 *
 * The response is the admin's re-resolved row, and it is frequently NOT what the caller's form
 * believed it was submitting: the backend applies the tier change FIRST and re-resolves everything
 * after it, so a demotion resets each module's tier in the same answer. Consume what comes back.
 */
export async function updateOrgMemberAccess(
  orgRef: string,
  targetUserId: string,
  body: UpdateOrgMemberAccessBody,
): Promise<unknown> {
  const { data } = await givmoClient.put({
    url: `${orgBase(orgRef)}/members/${encodeURIComponent(targetUserId)}/access`,
    body,
    throwOnError: true,
  });
  return data;
}

/**
 * Make ONE Global Admin a regular admin holding exactly the chosen apps (OM-57, D-CPO-64).
 * Global-Admin only; answers the person's re-resolved row, as the access PUT does.
 *
 * ⚠ NEVER FALLS BACK TO THE PUT. A backend without this route answers the family's route miss (404)
 * and writes nothing — which is the point of the route: the PUT's `is_global_admin: false` keeps
 * the old demotion's apps implicitly, the one thing the ruling forbids. So a failure here surfaces
 * as a failure, and the caller never retries it as a PUT.
 */
export async function changeOrgMemberRole(
  orgRef: string,
  targetUserId: string,
  body: OrgRoleChangeBody,
): Promise<unknown> {
  const { data } = await givmoClient.post({
    url: `${orgBase(orgRef)}/members/${encodeURIComponent(targetUserId)}/role-change`,
    body,
    throwOnError: true,
  });
  return data;
}

/**
 * Switch modules on/off org-wide. Global-Admin only, PARTIAL, and it answers the whole resolved map.
 *
 * ⚠ The body's key is `modules`. A class-specific console says something else for the same idea, and
 * the backend's write model forbids unknown keys — so a port-copy 422s rather than silently
 * no-op'ing, but only a request-shape check catches it before a person does.
 */
export async function updateOrgAccessSettings(
  orgRef: string,
  body: UpdateOrgAccessSettingsBody,
): Promise<unknown> {
  const { data } = await givmoClient.put({
    url: `${orgBase(orgRef)}/access/settings`,
    body,
    throwOnError: true,
  });
  return data;
}

/**
 * Take someone OFF this organization's admin team entirely. Global-Admin only.
 *
 * Answers 204 and carries no body. The organization's own class writer does the membership work —
 * the same one its class module's removal door calls — and the org console additionally tears down
 * the module grants and custom permission sets in the same transaction, so a re-added person starts
 * clean rather than inheriting whatever was configured for them before.
 *
 * ⚠ THIS IS NOT THE ACCESS EDITOR'S JOB, and the two are easy to confuse from the outside because
 * both can leave somebody with nothing. `updateOrgMemberAccess` with `has_access: false` on every
 * module takes their APPS away and leaves them on the team; the backend in fact REFUSES the last
 * one. This ends the membership. The editor's own last-module copy points here for exactly that
 * reason.
 *
 * ⚠ THREE REFUSALS, and only one of them is a failure. A **422 `LAST_GLOBAL_ADMIN_REFUSED`** means
 * the organization would be left with nobody who can administer it — see
 * {@link isOrgLastGlobalAdminRefusal}. A **400** means that person is not an admin of this
 * organization, which is ALSO what a second removal of the same person answers: the operation is
 * idempotent in effect, so a caller that raced itself should read that as "already gone", not as an
 * error worth a red message. A 404 is the family's absent-console answer as always.
 */
export async function removeOrgMember(orgRef: string, memberId: string): Promise<void> {
  await givmoClient.delete({
    url: `${orgBase(orgRef)}/members/${encodeURIComponent(memberId)}`,
    throwOnError: true,
  });
}

/**
 * Set the organization's measurement ids. Global-Admin only, PARTIAL, and THREE-VALUED per field.
 *
 * ⚠ THE BODY MUST CARRY A KEY ONLY FOR A FIELD IT MEANS TO CHANGE OR CLEAR. An absent key leaves the
 * stored id alone; an explicit `null` clears it. Those are opposite instructions that look identical
 * in a form, so the body is built by `buildMeasurementPatch` (`org-measurement-model.ts`) from the
 * draft AND the stored state — never by serializing the draft on its own.
 *
 * Answers the state AFTER the write, which is what lets a non-optimistic save be honest: the response
 * carries the fresh `updated_at` and `updated_by_user_id` alongside the ids. A field patched to the
 * value it already holds is not a change, so a save of an unedited form writes nothing at all.
 */
export async function updateOrgMeasurementConfig(
  orgRef: string,
  body: UpdateOrgMeasurementConfigBody,
): Promise<unknown> {
  const { data } = await givmoClient.put({
    url: `${orgBase(orgRef)}/measurement`,
    body,
    throwOnError: true,
  });
  return data;
}

// ── Shell contract v3: access requests (+4), preferences (+2), module terms (+2) ─────────────
//
// Eight more hand-written operations, for the same reason as the fifteen above: they landed on the
// backend after the committed snapshot was cut, and a refresh for one family is the over-reach the
// api-client doc warns about. Every one hangs under `orgBase`; the two preference operations sit
// under `me/` so they read as the caller's own.

/** `/org/{org_ref}/access-requests` — the one request table both views read. */
function accessRequestsBase(orgRef: string): string {
  return `${orgBase(orgRef)}/access-requests`;
}

/** What a request-list read needs: the page, an optional module filter, an optional status filter. */
export interface AccessRequestsRead extends PagedRead {
  /** Only this module's rows — REQUIRED for a caller who lacks `WRITE_MEMBER_ACCESS` (else 422). */
  module?: string | null;
  status?: string | null;
}

/**
 * Ask for a ROLE in a module. Any admin of the organization who does not hold the module.
 *
 * Idempotent: an open request is answered unchanged (200) and a new one created (201); the caller
 * cannot tell the two apart from here and does not need to — the standing read carries the row
 * either way. A holder of the module (a Global Admin holds every one) is refused 409; a module the
 * class does not carry is 422.
 */
export async function createOrgAccessRequest(
  orgRef: string,
  body: CreateOrgAccessRequestBody,
): Promise<unknown> {
  const { data } = await givmoClient.post({
    url: accessRequestsBase(orgRef),
    body,
    throwOnError: true,
  });
  return data;
}

/**
 * The request table, PENDING first then newest, paged. Two admissions: `WRITE_MEMBER_ACCESS` reads
 * every module; a holder of `<module>:approve_access_requests` reads that module's rows and MUST
 * pass `module` — the server answers 422 to an unfiltered list from such a caller.
 */
export async function fetchOrgAccessRequests(
  orgRef: string,
  read: AccessRequestsRead,
): Promise<unknown> {
  const query: Record<string, string | number> = page(ORG_CONSOLE_PAGE_LIMIT, read.offset);
  if (typeof read.module === 'string' && read.module.length > 0) query.module = read.module;
  if (typeof read.status === 'string' && read.status.length > 0) query.status = read.status;
  const { data } = await givmoClient.get({
    url: accessRequestsBase(orgRef),
    query,
    signal: read.signal,
    throwOnError: true,
  });
  return data;
}

/**
 * Approve one request — a GRANT, written through the same writer an access edit uses, plus the
 * row's flip and the record's event, in ONE unit of work on the server.
 *
 * ⚠ IT NEVER TURNS THE MODULE ON. A request on a module the organization has `off` or
 * `setting-up` is refused 409; the door is the switch, which is the one path that presents the
 * module's terms and starts its provisioning. A requester who has since left is 409 too, and the
 * row is VOIDed by the same call.
 */
export async function approveOrgAccessRequest(
  orgRef: string,
  requestId: string,
  body: ApproveOrgAccessRequestBody = {},
): Promise<unknown> {
  const { data } = await givmoClient.post({
    url: `${accessRequestsBase(orgRef)}/${encodeURIComponent(requestId)}/approve`,
    body,
    throwOnError: true,
  });
  return data;
}

/** Decline one request, with an optional note the requester will read. */
export async function denyOrgAccessRequest(
  orgRef: string,
  requestId: string,
  body: DenyOrgAccessRequestBody = {},
): Promise<unknown> {
  const { data } = await givmoClient.post({
    url: `${accessRequestsBase(orgRef)}/${encodeURIComponent(requestId)}/deny`,
    body,
    throwOnError: true,
  });
  return data;
}

/** The caller's own preference definitions + stored values. Any admin of the organization. */
export async function fetchOrgAdminPreferences(
  orgRef: string,
  options: { signal?: AbortSignal } = {},
): Promise<unknown> {
  const { data } = await givmoClient.get({
    url: `${orgBase(orgRef)}/me/preferences`,
    signal: options.signal,
    throwOnError: true,
  });
  return data;
}

/**
 * Write the caller's own preferences. PARTIAL and THREE-VALUED per key — see the body type. Answers
 * the whole `{definitions, values}` state after the write, which the mutation writes into the cache.
 */
export async function updateOrgAdminPreferences(
  orgRef: string,
  body: UpdateOrgAdminPreferencesBody,
): Promise<unknown> {
  const { data } = await givmoClient.put({
    url: `${orgBase(orgRef)}/me/preferences`,
    body,
    throwOnError: true,
  });
  return data;
}

/**
 * The instrument a module binds for this organization's class, and whether it has been accepted.
 * Any admin may read it. The console's own 404 means the pair binds NO instrument (dark by absence),
 * and every caller treats that as "nothing to accept", never as an error.
 */
export async function fetchOrgModuleTerms(
  orgRef: string,
  module: string,
  options: { signal?: AbortSignal } = {},
): Promise<unknown> {
  const { data } = await givmoClient.get({
    url: `${orgBase(orgRef)}/modules/${encodeURIComponent(module)}/terms`,
    signal: options.signal,
    throwOnError: true,
  });
  return data;
}

/**
 * Accept the instrument at the version just read — the same tier as the switch. A stale hash 409s;
 * accepting the current version twice is idempotent. Turning the module OFF later never un-accepts.
 */
export async function acceptOrgModuleTerms(
  orgRef: string,
  module: string,
  body: AcceptOrgModuleTermsBody,
): Promise<unknown> {
  const { data } = await givmoClient.post({
    url: `${orgBase(orgRef)}/modules/${encodeURIComponent(module)}/terms/accept`,
    body,
    throwOnError: true,
  });
  return data;
}

/**
 * The organization's setting definitions + stored values — seam A (Org Machinery OM-17). Served
 * per caller: a definition arrives iff the class carries its module, the organization has it ON
 * and the caller holds its `requires` string. The console's own 404 means the surface is ABSENT
 * (the backend half lands with the first definition); every caller reads that as "nothing served".
 */
export async function fetchOrgSettings(
  orgRef: string,
  options: { signal?: AbortSignal } = {},
): Promise<unknown> {
  const { data } = await givmoClient.get({
    url: `${orgBase(orgRef)}/settings`,
    signal: options.signal,
    throwOnError: true,
  });
  return data;
}

/**
 * Write the organization's settings. PARTIAL and THREE-VALUED per key, each key guarded on the
 * revision it was read at — see the body type. Answers the whole `{definitions, values, state}` after
 * the write, which the mutation writes into the cache.
 */
export async function updateOrgSettings(
  orgRef: string,
  body: UpdateOrgSettingsBody,
): Promise<unknown> {
  const { data } = await givmoClient.put({
    url: `${orgBase(orgRef)}/settings`,
    body,
    throwOnError: true,
  });
  return data;
}

/**
 * Deactivate the organization — the org-account deactivation act (Org Machinery OM-9).
 *
 * ⚠ A 200 IS THE CALLER'S LAST AUTHENTICATED RESPONSE. The act ends every admin's standing in ONE
 * transaction (the caller's included) and the server ends their sessions after it, so nothing on
 * this surface may be read afterwards: the host signs the person out and takes them to its class
 * door. A second call answers the console's route-miss 404 by construction — nobody holds the
 * capability once the act has landed — and the operator's heal is a service act, never a wire call.
 * No body; the organization is the whole instruction.
 */
export async function deactivateOrg(orgRef: string): Promise<unknown> {
  const { data } = await givmoClient.post({
    url: `${orgBase(orgRef)}/deactivate`,
    throwOnError: true,
  });
  return data;
}
